import { jsonParse } from '@bigo/helpers';
import { defineStore } from 'pinia';

import {
  AccountPermission,
  AccountRole,
  AccountSubRole,
  AgentUserTypeReport,
} from '@/constants/enum';
import {
  liveAgentUnionOssGetAgencyMemberRole,
  liveAgentManageAnchorQryAnchorRelationChgTabVisible,
  liveAgentFrontGetRecomAnchorEntry,
} from '@/api';
import { AGENT_ROLE } from '@/constants';

const TOKEN_STORAGE_KEY = 'token';
const REGIONS_STORAGE_KEY = 'regions';

/**
 * @typedef {Object} User
 * @prop {string} account
 * @prop {string} bigoId
 * @prop {string} bigoUid
 * @prop {number} isChild
 * @prop {string} mobilePhone
 * @prop {string} region
 * @prop {string} regionId
 * @prop {import('@/constants/enum').AccountRole} role
 * @prop {import('@/constants/enum').AccountStatus} status
 * @prop {import('@/constants/enum').AccountPermission[]} permissions
 * @prop {string} token
 * @prop {string} username
 * @prop {number} isWhite
 *
 * @typedef {Object} Region
 * @prop {string} id
 * @prop {string} name
 * @prop {string} shortName
 */

export const useAppStore = defineStore('app', {
  state: () => ({
    /** @type {User} */
    loginUser: null,
    /** @type {string} */
    token: localStorage.getItem(TOKEN_STORAGE_KEY),
    /** @type {Region[]} */
    regions: jsonParse(localStorage.getItem(REGIONS_STORAGE_KEY)) || [],
    tokenErrCnt: 0,
    phoneAuthCnt: 0,
    phoneAuthSuccessCnt: 0,
    hasTransferPersmission: false,
    hasTransferInOutPermission: false,
    hasTerminateAnchorPermission: false,
    phoneInfo: {
      prefix: '',
      phone: '',
    },
    agentList: [],
    agentRole: AGENT_ROLE.NOT_AGENT,
    selfBigoId: '',
    bigoUid: '',
    unModifyBigoId: '',
    hasRecommendAnchorEntry: false,
  }),
  getters: {
    isRole() {
      return (role) => {
        if (!this.loginUser) return false;

        return Array.isArray(role)
          ? role.includes(this.loginUser.role)
          : this.loginUser.role === role;
      };
    },
    isSubRole() {
      return (subRole) => {
        if (!this.loginUser) return false;

        return Array.isArray(subRole)
          ? subRole.includes(this.loginUser.subRole)
          : this.loginUser.subRole === subRole;
      };
    },
    isAdmin() {
      return this.isRole(AccountRole.Admin);
    },
    isOperator() {
      return this.isRole(AccountRole.Operator);
    },
    isAgency() {
      return this.isRole(AccountRole.Agency);
    },
    isAgencyAgent() {
      return this.isAgency && this.loginUser.subRole === AccountSubRole.SuperAdmin;
    },
    isAgencySubAgent() {
      return this.isAgency && this.loginUser.subRole === AccountSubRole.Admin;
    },
    isAgencyManager() {
      return this.isAgency && this.loginUser.subRole === AccountSubRole.Manager;
    },
    userType() {
      if (this.isAdmin) {
        return AgentUserTypeReport.SuperAdmin;
      }
      if (this.isOperator) {
        return AgentUserTypeReport.Operator;
      }
      if (this.isAgencyAgent) {
        return AgentUserTypeReport.AgencyAgent;
      }
      if (this.isAgencySubAgent) {
        return AgentUserTypeReport.AgencySubAgent;
      }
      if (this.isAgencyManager) {
        return AgentUserTypeReport.AgencyManager;
      }
      return AgentUserTypeReport.NoProfile;
    },
    isWhite() {
      return !!this.loginUser?.isWhite;
    },
    hasPermissionIn() {
      return (permissions) => {
        permissions = Array.isArray(permissions) ? permissions : [permissions];
        if (!this.loginUser) return false;
        const userPermissions = [
          ...this.loginUser.permissions,
          this.hasTransferPersmission ? AccountPermission.TransferRelation : undefined,
        ];
        return userPermissions.some((permission) => permissions.includes(permission));
      };
    },
    hasRegionIn() {
      return (regions) => {
        regions = Array.isArray(regions) ? regions : [regions];
        if (!this.loginUser) return false;
        return this.regions.some((region) => regions.includes(region.shortName));
      };
    },
    regionOptions() {
      return this.regions.map((region) => ({
        label: region.name,
        value: region.id,
      }));
    },
    agentOptions() {
      return this.agentList.map((item) => {
        return {
          label: item.name,
          value: item.uid,
        };
      });
    },
    regionOptionsAbbr() {
      return this.regions.map((region) => ({
        label: region.shortName,
        value: region.id,
      }));
    },
    regionOptionsAbbrName() {
      return this.regions.map((region) => region.shortName);
    },
    hasBindedPhone() {
      return !!this.phoneInfo.phone;
    },
  },
  actions: {
    setSelfBigo(val) {
      this.selfBigoId = val;
      console.log('this.selfBigoId', this.selfBigoId);
    },
    setUnModifyBigoId(val) {
      this.unModifyBigoId = val;
    },
    setBigoUid(val) {
      this.bigoUid = val;
      console.log('this.bigoUid', this.bigoUid);
    },
    setToken(token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      this.token = token;
    },
    setTokenErrCnt() {
      this.tokenErrCnt += 1;
    },
    setPhoneAuthCnt() {
      this.phoneAuthCnt += 1;
    },
    setPhoneAuthSuccess() {
      this.phoneAuthSuccessCnt += 1;
    },
    setPhoneInfo(val) {
      this.phoneInfo.prefix = val.prefix;
      this.phoneInfo.phone = val.phone;
    },
    setLoginUser(user) {
      this.loginUser = user;
    },
    async getTransferPermission() {
      if (this.hasTransferPersmission) return true;
      const { code, data } = await liveAgentManageAnchorQryAnchorRelationChgTabVisible();
      if (code === 0) {
        if (data.transfer >= 1 || data.cancel >= 1) {
          this.hasTransferPersmission = true;
        }
        this.hasTransferInOutPermission = data.transfer === 1;
        this.hasTerminateAnchorPermission = data.cancel === 1;
      }
    },
    setRegions(regions) {
      localStorage.setItem(REGIONS_STORAGE_KEY, JSON.stringify(regions));
      this.regions = regions;
    },
    async getAgentList() {
      const { code, data } = await liveAgentUnionOssGetAgencyMemberRole();
      if (code === 0) {
        this.agentList = data?.member_list || [];
      }
    },
    logout() {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(REGIONS_STORAGE_KEY);
      this.token = null;
      this.loginUser = null;
      this.hasTransferPersmission = false;
      this.regions = [];
      this.hasTransferInOutPermission = false;
      this.hasTerminateAnchorPermission = false;
    },
    setAgentRole(role) {
      this.agentRole = role;
    },
    async checkRecommendAnchorEntry() {
      const res = await liveAgentFrontGetRecomAnchorEntry();
      if (res.code === 0) {
        this.hasRecommendAnchorEntry = res.data?.entry;
      }
    },
  },
});
